/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from '@angular/core';
import { AgFns, GridOptionsExt, EntityFinderDialog, EntityFinderParams, ISortModel } from '@core';

import { ActiveStatusEnum, ProductTypeConfig, ProgramTypeEnum } from '@models';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { UtilFns } from '@utils';
import { ColDef } from '@ag-grid-community/core';
import { AccountDbQueryService } from '../services/account-db-query.service';

export interface ProductTypeConfigFinderParams extends EntityFinderParams {
  accountId?: string;
  productTypeConfigs?: ProductTypeConfig[];
  programTypeId?: ProgramTypeEnum;
}

@Component({
  selector: 'prox-product-type-config-finder',
  template: EntityFinderDialog.HTML,
  standalone: false,
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class ProductTypeConfigFinderDialog extends EntityFinderDialog<ProductTypeConfig, ProductTypeConfigFinderParams> {
  params!: ProductTypeConfigFinderParams;

  constructor(
    ref: DynamicDialogRef,
    override config: DynamicDialogConfig<ProductTypeConfigFinderParams>,
    public dbQueryService: AccountDbQueryService,
  ) {
    super(ref, config);
    UtilFns.assertNonNull(config.data);
    this.params = config.data;
  }

  override async initialize() {
    return {
      header: 'Select Product Groups',
      width: 'calc(80vw)',
    };
  }

  override async initDatasource() {
    if (this.params.productTypeConfigs) {
      return this.params.productTypeConfigs;
    } else if (this.params.accountId != null) {
      // Filter by isInventory flag based on program type (only when programTypeId is provided)
      let isInventory: boolean | undefined;
      if (this.params.programTypeId != null) {
        isInventory = this.params.programTypeId === ProgramTypeEnum.Inventory;
      }
      const queryFn = () =>
        this.dbQueryService
          .createProductTypeConfigsQuery(this.params.accountId || '', null, false, isInventory)
          .where({ activeStatusId: ActiveStatusEnum.Active });
      return AgFns.buildDatasource(queryFn);
    } else {
      throw new Error('This finder needs either an account id or an array of ProductTypeConfigs');
    }
  }

  override getColDefsAndSortModel() {
    const colDefs = [
      { headerName: 'Supplier', field: 'pricedProductType.productType.supplier.name', filter: 'agTextColumnFilter' },
      { headerName: 'Manufacturer', field: 'pricedProductType.productType.manufacturer.name', filter: 'agTextColumnFilter' },
      { headerName: 'SKU', field: 'pricedProductType.productType.productKey', filter: 'agTextColumnFilter' },
      // I like this checkbox here too!  Please retain.
      // The new AG Grid 32+ doesn't support more than a single checkbox column because it is now set on the gridOptions.rowSelection property.
      { headerName: 'Config / Catalog', field: 'name', filter: 'agTextColumnFilter' },
      { headerName: 'Name', field: 'pricedProductType.productType.name', filter: 'agTextColumnFilter' },
      { headerName: 'Description', field: 'pricedProductType.productType.description', filter: 'agTextColumnFilter' },
      { headerName: 'Base Price', field: 'pricedProductType.basePrice' },
    ] as ColDef[];
    const sortModel = [
      { colId: 'name', sort: 'asc' },
    ] as ISortModel;
    return [colDefs, sortModel] as const;
  }
}
